/**
 * buy-box.js — <buy-box> single-option (Size) picker for the PDP.
 *
 * Selecting a size updates: the hidden input[name="id"] (what UpCart / Shopify actually add),
 * the active state + aria-pressed, the price readout, which CTA shows (add vs notify), and ?variant=.
 * Sold-out sizes are still clickable: selecting one shows the "Sold out — Get notified" CTA and
 * opens the back-in-stock modal (dispatches `sundae:notify-open`; assets/stoq-notify.js drives it).
 * Everything is scoped to the element (no global #product-form selector — deliberately avoiding the
 * old PDP's duplicate-id binding bug).
 *
 * ⚠️ The notify CTA is a SEPARATE button (type="button", outside the {% form %}) — NOT the submit
 * button. UpCart intercepts the add-to-cart button's click at the capture phase, so that click never
 * reaches this listener; a plain button outside the form is the only thing we can hook.
 *
 * Plain HTMLElement + IIFE + <script defer>, matching the theme's other components (not Vite).
 */
(function () {
  if (customElements.get('buy-box')) return;

  class BuyBox extends HTMLElement {
    connectedCallback() {
      this.priceEl = this.querySelector('[data-bb-price]');
      this.compareEl = this.querySelector('[data-bb-compare]');
      this.saleBadge = this.querySelector('[data-bb-badge-sale]');
      this.discountEl = this.querySelector('[data-bb-discount]');
      this.idInput = this.querySelector('[data-bb-id]');
      this.addBtn = this.querySelector('[data-bb-atc-add]');
      this.notifyBtn = this.querySelector('[data-bb-atc-notify]');
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      // Show the right CTA for the initially-active size (server marks one .is-active): the real
      // add-to-cart when in stock, or the notify button when sold out. State only — does NOT open
      // the modal on load.
      var active = this.querySelector('[data-bb-size].is-active');
      if (active) {
        this.applyVariantState(active);
        this.applySaleState(active);
      } else if (this.idInput) {
        // Default-variant products (bags, bandanas, accessories) render NO size buttons —
        // bb.liquid gates the picker on `unless product.has_only_default_variant`. Without this
        // branch currentVariantId stays undefined, so a sold-out one dispatched
        // `sundae:notify-open { variantId: undefined }` and stoq-notify.js dropped it at its
        // null guard: the notify CTA looked alive but opened nothing. The hidden input always
        // carries current.id, so take it from there. Price / sale state need no fallback — with
        // one variant the server-rendered values are already correct and can never change.
        this.currentVariantId = this.idInput.value;
      }

      this.announceReady();
    }

    /**
     * Announce "a buy box for THIS product just appeared, and it has a sold-out size" — a pure
     * PERFORMANCE hint, fired once on connect. Nothing depends on it for correctness.
     *
     * Why it exists: a back-in-stock app has to re-fetch product data before it can show the right
     * modal, which measured ~870ms on the first click in the quick-view drawer. Announcing on
     * connect lets the bridge do that fetch while the shopper is still reading the product, so the
     * click itself is instant. Same app-agnostic contract as the other two events — we state what
     * exists, the bridge decides what to do about it.
     *
     * Silent when nothing is sold out: that buy box can never open the notify flow, so warming
     * anything for it would be a wasted request.
     */
    announceReady() {
      var handle = this.getAttribute('data-bb-handle');
      // No notify CTA (external product) → no notify flow → nothing to warm.
      if (!handle || !this.notifyBtn) return;

      var hasSoldOut = !!this.querySelector('[data-bb-size][data-available="false"]');
      // Default-variant products (bags, bandanas) render NO size buttons — there, the notify CTA
      // being visible is the only signal that this product is sold out.
      if (!hasSoldOut && this.notifyBtn && !this.notifyBtn.hidden) hasSoldOut = true;
      if (!hasSoldOut) return;

      this.dispatchEvent(
        new CustomEvent('sundae:buy-box-ready', {
          detail: { handle: handle, hasSoldOutVariant: true },
          bubbles: true
        })
      );
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      var sizeBtn = event.target.closest('[data-bb-size]');
      if (sizeBtn && this.contains(sizeBtn)) {
        event.preventDefault();
        this.selectSize(sizeBtn); // sold-out sizes are handled here too (they open the notify modal)
        return;
      }
      // Notify CTA — a plain button OUTSIDE the form, so UpCart doesn't swallow its click (unlike the
      // real add-to-cart button, whose click UpCart intercepts at the capture phase). Opens the modal.
      // Colour swatches are plain links (sibling-navigate) — no JS needed; the browser handles them.
      var notifyBtn = event.target.closest('[data-bb-atc-notify]');
      if (notifyBtn && this.contains(notifyBtn)) {
        event.preventDefault();
        this.openNotify();
        return;
      }
    }

    selectSize(btn) {
      var sizes = this.querySelectorAll('[data-bb-size]');
      for (var i = 0; i < sizes.length; i++) {
        var on = sizes[i] === btn;
        sizes[i].classList.toggle('is-active', on);
        sizes[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }

      var id = btn.getAttribute('data-variant-id');
      if (this.idInput) this.idInput.value = id;
      if (this.priceEl) this.priceEl.textContent = btn.getAttribute('data-price');
      this.applySaleState(btn);

      this.applyVariantState(btn);

      // Reflect the choice in the URL so refresh / share lands on the right variant.
      // Skipped in the quick-view drawer: that buy box is for a DIFFERENT product than the page the
      // visitor is on, so rewriting the address bar there would be wrong. Must be an `if` around the
      // rewrite, NOT an early return — the sold-out notify call below still has to run in the drawer.
      if (this.getAttribute('data-bb-context') !== 'quick-view') {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState({}, '', url);
        } catch (e) {
          /* no-op */
        }
      }

      // Tell any app bridge that the variant changed (assets/kaching-sync.js today). Dispatched
      // AFTER the form input + URL are written, so a listener reading either sees the new value.
      this.announceVariant();

      // A sold-out size IS the notify trigger — clicking it opens the modal directly.
      if (this.notifyMode) this.openNotify();
    }

    /**
     * Sync everything sale-related to the selected variant: the struck compare-at price, the % off
     * badge, and whether the badge row shows at all.
     *
     * Sale state is PER VARIANT — one size can be discounted while another sits at full price — so
     * leaving these at their server-rendered values would strand a stale "45% OFF" beside a
     * full-price size. Liquid writes both data attributes EMPTY when a variant isn't discounted,
     * which is the not-on-sale signal.
     */
    applySaleState(btn) {
      var compare = btn.getAttribute('data-compare-at');
      var discount = btn.getAttribute('data-discount');
      var onSale = !!compare && !!discount;

      if (this.compareEl) {
        if (onSale) this.compareEl.textContent = compare;
        this.compareEl.hidden = !onSale;
      }
      if (this.discountEl && onSale) this.discountEl.textContent = discount;
      // The sale badge now lives INSIDE .bb__price and hides itself; there is no badge row to
      // collapse any more, and the callout badge is static markup with no JS involvement at all.
      if (this.saleBadge) this.saleBadge.hidden = !onSale;
    }

    /**
     * Show the CTA that matches a variant's availability — only ONE is visible at a time:
     *   available → the real add-to-cart (submit; UpCart handles it)
     *   sold out  → the "Sold out — Get notified" button (opens the notify modal)
     */
    applyVariantState(btn) {
      var available = btn.getAttribute('data-available') !== 'false';
      // No notify CTA rendered (external product — bb.liquid swaps it for a link out) → a sold-out
      // size just selects; it must not open the back-in-stock modal.
      this.notifyMode = !available && !!this.notifyBtn;
      this.currentVariantId = btn.getAttribute('data-variant-id');
      if (this.addBtn) this.addBtn.hidden = !available;
      if (this.notifyBtn) this.notifyBtn.hidden = available;
    }

    /**
     * Ask whoever owns back-in-stock (assets/stoq-notify.js today) to open the notify flow for the
     * current variant. The buy-box stays app-agnostic — it never names StoQ. Bubbles to document,
     * where stoq-notify.js listens.
     *
     * `handle` says WHICH PRODUCT this buy box is for, and it is required, not decorative: a
     * back-in-stock app keys off the PAGE's product, while the quick-view drawer renders a different
     * one. Without it the drawer's notify opened the modal for the PDP's product — and when the PDP
     * was fully in stock, for nothing at all (verified live 2026-09-03). Stating the product keeps
     * this app-agnostic; re-pointing the app is the bridge's job, not ours.
     */
    openNotify() {
      this.dispatchEvent(
        new CustomEvent('sundae:notify-open', {
          detail: {
            variantId: this.currentVariantId,
            handle: this.getAttribute('data-bb-handle') || null
          },
          bubbles: true
        })
      );
    }

    /**
     * Announce "the shopper picked a different variant" to whoever is bridging an app
     * (assets/kaching-sync.js today). Same app-agnostic contract as openNotify(): the buy-box never
     * names the app, it only states what happened. `context` is passed through so a listener can
     * ignore the quick-view drawer, which is a DIFFERENT product from the one on the page.
     */
    announceVariant() {
      this.dispatchEvent(
        new CustomEvent('sundae:variant-change', {
          detail: {
            variantId: this.currentVariantId,
            context: this.getAttribute('data-bb-context') || 'pdp'
          },
          bubbles: true
        })
      );
    }
  }

  customElements.define('buy-box', BuyBox);
})();
